'use client';

import CustomLoading from '@/components/custom-loading';
import { initialPageInfo } from '@/components/custom-table-ag';
import CustomToast from '@/components/custom-toast';
import { useEmployeeInfoStore } from '@/hooks/use-employee-store';
import { cn } from '@/lib/utils';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { readDossierNotifications } from '@/server/api/v1/dossier-notification/dossier-notification.action';
import { DossierNotificationResponse } from '@/server/api/v1/dossier-notification/type';
import {
    ChildLogicalOperator,
    FilterBuilders,
    LogicalOperator,
    OperatorType,
} from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import {
    useDossierNotificationStore,
    useHeaderNotificationDropdownStore,
} from '../utils';
import DossierNotificationList from './dossier-notification-list';

const createFilter = ({
    empId,
    isCust,
    userId,
}: {
    isCust?: boolean;
    userId?: string;
    empId?: string;
}) => {
    let filters: FilterBuilders<DossierNotificationResponse>[] = [
        {
            key: isCust ? 'Customer_ID' : 'EmployeeId',
            values: [isCust ? userId! : empId!],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        },
        {
            key: 'IsAborted',
            values: [true],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.NotEqual,
        },
    ];

    if (isCust) {
        filters.push({
            key: 'Customer_IsVisible',
            values: [true],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        });
        filters.push({
            key: 'Hidden_Customer',
            values: [true],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.NotEqual,
        });
    } else if (userId) {
        filters.push({
            key: 'Hidden_UserIDs',
            values: [userId],
            logicalOperator: LogicalOperator.And,
            childLogicalOperator: ChildLogicalOperator.And,
            operatorType: OperatorType.Regex,
            isNot: true,
        });
    }

    return filters;
};

function DossierNotification({
    className,
    enableAPI = false,
}: {
    className?: string;
    enableAPI?: boolean;
}) {
    const transCmp = useTranslations('NotificationList');

    const empData = useEmployeeInfoStore((x) => x.current);
    const authData = useAuthenticationStore((x) => x.auth);

    const { isCustomer } = useHeaderNotificationDropdownStore((x) => x);
    const { totalPage, arrNotice, setStoreState } = useDossierNotificationStore(
        (x) => x
    );

    const [pageInfo, setPageInfo] = useState(initialPageInfo);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isMarkingAll, setIsMarkingAll] = useState(false);

    const idToFetch = isCustomer ? authData?.CustomerId : authData?.UserId;

    const {
        data: apiNotification,
        isLoading,
        isFetching,
    } = api.dossierNotification.GetPage.useQuery(
        {
            ...pageInfo,
            filters: createFilter({
                empId: empData?.ID,
                isCust: isCustomer,
                userId: idToFetch,
            }),
        },
        {
            enabled: enableAPI && !!(empData || idToFetch),
        }
    );

    const onFetchMoreNotice = useCallback(() => {
        setIsFetchingMore(true);
        setPageInfo((st) => {
            const newPage = st.pageNumber ?? 1;
            return { ...st, pageNumber: newPage + 1 };
        });
    }, []);

    /**Đánh dấu tất cả thông báo đã đọc — chỉ dành cho nhân viên */
    const onMarkAllRead = useCallback(async () => {
        if (!empData?.ID || isCustomer) return;

        const unreadCodes = arrNotice
            .filter((n) => !n.IsSeen && n.Code)
            .map((n) => n.Code!);

        if (!unreadCodes.length) return;

        setIsMarkingAll(true);
        const result = await readDossierNotifications({
            Codes: unreadCodes,
            EmployeeID: empData.ID,
            Client: 'MotCuaWebApp',
        });

        if (result?.data?.isSuccessStatus) {
            setStoreState((st) => ({
                arrNotice: (st?.arrNotice ?? []).map((n) => ({
                    ...n,
                    IsSeen: true,
                })),
                totalUnRead: 0,
            }));
            CustomToast.sonner.success(transCmp('MarkAllSeenSuccess'), {
                duration: 3000,
                position: 'top-right',
            });
        }
        setIsMarkingAll(false);
    }, [arrNotice, empData?.ID, isCustomer, setStoreState, transCmp]);

    useEffect(() => {
        if (isFetching || isLoading) return;
        if (apiNotification?.data) {
            const { Items, ...rest } = apiNotification.data;

            setStoreState((st) => {
                return {
                    arrNotice:
                        rest.PageNumber === 1
                            ? (Items ?? [])
                            : [...(st?.arrNotice ?? []), ...(Items ?? [])],
                    totalNotification: rest.TotalCount,
                    pageNumber: rest.PageNumber,
                    totalPage: rest.TotalPages,
                };
            });
            setIsFetchingMore(false);
        }
    }, [apiNotification, isFetching, isLoading, setStoreState]);

    return (
        <CustomLoading.Wrapper
            className={cn('min-h-40 p-0', className)}
            loading={(isLoading || isFetching) && !isFetchingMore}
        >
            <DossierNotificationList
                isFetching={isFetching}
                isFetchMore={isFetchingMore}
                onFetchMore={onFetchMoreNotice}
                hvMoreNotice={pageInfo.pageNumber! < totalPage}
                onMarkAllRead={onMarkAllRead}
                isMarkingAll={isMarkingAll}
            />
        </CustomLoading.Wrapper>
    );
}

export default DossierNotification;
